/**
 * CashFlowWeekTable — minimal weekly breakdown of cash-flow buckets.
 *
 * June 2026 redesign (user feedback: "fa cagare così, più minimal"):
 * the previous version dumped every week verbatim — 13/26/52 rows of
 * "0 € · 0 € · 0 €" when the underlying account had no transactions in
 * the period. The new design follows the inbox / docs minimalism:
 *
 *   - HIDE consecutive empty weeks behind a single muted divider row
 *     ("3 settimane senza movimenti") so the eye lands on what changed
 *   - drop the per-cell horizontal bars — too much chrome for tiny
 *     numbers; a single full-row magnitude indicator on the right tells
 *     the same story without the noise
 *   - en-dash ("—") instead of "0 €" for empty cells so they recede
 *   - format week as a range ("4 — 10 mag") instead of "lun 04 mag"
 *   - current week is highlighted with a left accent border + "ora"
 *     pill (NOT a wash) — mirrors :class:`ListItemRow` selection chrome
 *
 * Edge cases:
 *   - week_start parsed leniently (BE may emit ISO date or full ISO 8601)
 *   - current-week detection works across timezones (uses local midnight)
 *   - empty input renders an inline "nessun movimento" hint
 */

import * as React from "react";
import { Plural, Trans } from "@lingui/react/macro";

import { cn } from "@ando/ui";

import type { CashFlowBucket } from "@ando/core";


function formatEur(value: number, currency = "EUR"): string {
  return new Intl.NumberFormat("it-IT", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
    signDisplay: "auto",
  }).format(value);
}


function parseWeekStart(iso: string): Date | null {
  // Accept "YYYY-MM-DD" (BE canonical) or full ISO 8601. Always
  // resolve to LOCAL midnight so "current week" comparisons match
  // the user's wall clock.
  if (!iso) return null;
  const datePart = iso.length >= 10 ? iso.slice(0, 10) : iso;
  const parts = datePart.split("-").map((v) => Number.parseInt(v, 10));
  const y = parts[0];
  const m = parts[1];
  const d = parts[2];
  if (
    y === undefined ||
    m === undefined ||
    d === undefined ||
    !Number.isFinite(y) ||
    !Number.isFinite(m) ||
    !Number.isFinite(d)
  ) {
    const fallback = new Date(iso);
    return Number.isNaN(fallback.getTime()) ? null : fallback;
  }
  return new Date(y, m - 1, d);
}


/** Italian week range — "4 — 10 mag" / "29 dic — 4 gen". */
function formatWeekRange(iso: string): string {
  const start = parseWeekStart(iso);
  if (!start) return iso;
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  const monthFmt = new Intl.DateTimeFormat("it-IT", { month: "short" });
  const sMonth = monthFmt.format(start).replace(".", "");
  const eMonth = monthFmt.format(end).replace(".", "");
  const sDay = start.getDate();
  const eDay = end.getDate();
  if (sMonth === eMonth) return `${sDay} — ${eDay} ${eMonth}`;
  return `${sDay} ${sMonth} — ${eDay} ${eMonth}`;
}


/** ``true`` when ``iso`` is the Monday of the local-calendar week
 *  containing ``now``. Works in any timezone — uses local midnight on
 *  both sides of the comparison. */
function isCurrentWeek(iso: string, now: Date): boolean {
  const start = parseWeekStart(iso);
  if (!start) return false;
  // Local-midnight of "now" so we compare day boundaries, not millis.
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diffDays = Math.floor(
    (today.getTime() - start.getTime()) / (24 * 3600 * 1000),
  );
  return diffDays >= 0 && diffDays < 7;
}


function hasMovement(b: CashFlowBucket): boolean {
  const inflow = Number.isFinite(b.inflow) ? Math.abs(b.inflow) : 0;
  const outflow = Number.isFinite(b.outflow) ? Math.abs(b.outflow) : 0;
  const net = Number.isFinite(b.net) ? Math.abs(b.net) : 0;
  return inflow > 0 || outflow > 0 || net > 0;
}


export interface CashFlowWeekTableProps {
  readonly buckets: readonly CashFlowBucket[];
  readonly currency?: string;
  readonly className?: string;
}


export const CashFlowWeekTable: React.FC<CashFlowWeekTableProps> = ({
  buckets,
  currency = "EUR",
  className,
}) => {
  const safe = Array.isArray(buckets) ? buckets : [];
  const now = React.useMemo(() => new Date(), []);

  if (safe.length === 0) {
    return (
      <p className={cn("text-[11.5px] text-ink-3", className)}>
        <Trans>Nessuna settimana nel periodo selezionato.</Trans>
      </p>
    );
  }

  // Walk the buckets and build the display list: each entry is either a
  // real bucket OR a "+N senza movimenti" gap. The current week ALWAYS
  // makes it through even when empty (so the user keeps the temporal
  // anchor on screen).
  type Entry =
    | { readonly kind: "row"; readonly bucket: CashFlowBucket; readonly isCurrent: boolean }
    | { readonly kind: "gap"; readonly count: number; readonly key: string };
  const entries: Entry[] = [];
  let gap = 0;
  let gapKey = "";
  for (const b of safe) {
    const current = isCurrentWeek(b.week_start, now);
    const movement = hasMovement(b);
    if (!movement && !current) {
      if (gap === 0) gapKey = `gap-${b.week_start}`;
      gap += 1;
      continue;
    }
    if (gap > 0) {
      entries.push({ kind: "gap", count: gap, key: gapKey });
      gap = 0;
    }
    entries.push({ kind: "row", bucket: b, isCurrent: current });
  }
  if (gap > 0) entries.push({ kind: "gap", count: gap, key: gapKey || "gap-tail" });

  // Scale the magnitude bar against the highest absolute movement in
  // the rendered period so the visual weight is comparable.
  const peak = Math.max(
    1,
    ...safe.map((b) =>
      Math.max(
        Number.isFinite(b.inflow) ? Math.abs(b.inflow) : 0,
        Number.isFinite(b.outflow) ? Math.abs(b.outflow) : 0,
      ),
    ),
  );

  return (
    <div className={cn("flex flex-col", className)}>
      {/* Column header — purely typographic; mirrors the inbox /
          documents list-header spacing. */}
      <div className="mb-1.5 grid grid-cols-[1fr_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)] gap-x-4 px-2 font-semibold text-[10px] text-ink-3 uppercase tracking-[0.06em]">
        <span><Trans>Settimana</Trans></span>
        <span className="text-right"><Trans>Entrate</Trans></span>
        <span className="text-right"><Trans>Uscite</Trans></span>
        <span className="text-right"><Trans>Netto</Trans></span>
      </div>
      <ul className="flex flex-col">
        {entries.map((e) =>
          e.kind === "gap" ? (
            <li
              key={e.key}
              aria-hidden
              className="flex items-center gap-3 px-2 py-1.5 text-[10.5px] text-ink-3"
            >
              <span className="h-px flex-1 bg-hairline" />
              <span className="shrink-0 italic">
                <Plural
                  value={e.count}
                  one="# settimana senza movimenti"
                  other="# settimane senza movimenti"
                />
              </span>
              <span className="h-px flex-1 bg-hairline" />
            </li>
          ) : (
            <Row
              key={e.bucket.week_start}
              bucket={e.bucket}
              peak={peak}
              currency={currency}
              isCurrent={e.isCurrent}
            />
          ),
        )}
      </ul>
    </div>
  );
};
CashFlowWeekTable.displayName = "CashFlowWeekTable";


interface RowProps {
  readonly bucket: CashFlowBucket;
  readonly peak: number;
  readonly currency: string;
  readonly isCurrent: boolean;
}


const Row: React.FC<RowProps> = ({ bucket, peak, currency, isCurrent }) => {
  const inflow = Number.isFinite(bucket.inflow) ? bucket.inflow : 0;
  const outflow = Number.isFinite(bucket.outflow) ? bucket.outflow : 0;
  const net = Number.isFinite(bucket.net) ? bucket.net : inflow - outflow;
  const hasIn = inflow !== 0;
  const hasOut = outflow !== 0;
  const hasNet = net !== 0;
  const magnitude = Math.max(Math.abs(inflow), Math.abs(outflow));
  const magnitudePct = peak > 0 ? Math.min(100, (magnitude / peak) * 100) : 0;
  return (
    <li
      className={cn(
        "relative grid grid-cols-[1fr_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)] items-center gap-x-4 border-hairline/60 border-b px-2 py-2 text-[12px] last:border-b-0",
        isCurrent &&
          "before:absolute before:inset-y-1 before:left-0 before:w-0.5 before:rounded-r-full before:bg-ando-orange",
      )}
    >
      <span className="flex items-center gap-1.5 text-ink-1">
        <span>{formatWeekRange(bucket.week_start)}</span>
        {isCurrent ? (
          <span className="inline-flex items-center rounded-full bg-ando-orange-soft px-1.5 py-0 font-medium text-[9.5px] text-ando-orange">
            <Trans>ora</Trans>
          </span>
        ) : null}
      </span>
      <span
        className={cn(
          "text-right tabular-nums",
          hasIn ? "font-medium text-emerald-700" : "text-ink-3",
        )}
      >
        {hasIn ? formatEur(Math.abs(inflow), currency) : "—"}
      </span>
      <span
        className={cn(
          "text-right tabular-nums",
          hasOut ? "font-medium text-rose-700" : "text-ink-3",
        )}
      >
        {hasOut ? formatEur(Math.abs(outflow), currency) : "—"}
      </span>
      <span className="flex flex-col items-end gap-1">
        <span
          className={cn(
            "tabular-nums",
            !hasNet
              ? "text-ink-3"
              : net > 0
                ? "font-semibold text-emerald-700"
                : "font-semibold text-rose-700",
          )}
        >
          {hasNet ? formatEur(net, currency) : "—"}
        </span>
        {magnitudePct > 0 ? (
          <span
            aria-hidden
            className={cn(
              "h-px rounded-full",
              net >= 0 ? "bg-emerald-300" : "bg-rose-300",
            )}
            style={{ width: `${Math.max(6, magnitudePct).toFixed(1)}%` }}
          />
        ) : null}
      </span>
    </li>
  );
};
